import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useEffect } from 'react';
import { z } from 'zod';

import { apiClient, isHubOutageError } from '@/lib/api-client';
import { type AgentSummary, useAuthStore } from '@/store/auth';

// Schema for `GET /api/v1/auth/me`. Same shape as `AgentOut` in
// `hub/app/api/auth.py`. Validate, don't cast — a regression there should
// surface as a typed error, not as random undefineds in render code.
const meResponseSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  display_name: z.string(),
  role: z.enum(['agent', 'admin']),
});

/** React Query key for the current-agent query. Exported so the 401
 *  interceptor / sign-out paths can invalidate it. */
export const currentAgentQueryKey = ['auth', 'me'] as const;

async function fetchCurrentAgent(): Promise<AgentSummary> {
  const response = await apiClient.get('/auth/me');
  return meResponseSchema.parse(response.data);
}

/** First `/auth/me` retry delay while the hub is out (spec 01 §3.6 item 4). */
export const AUTH_ME_RETRY_BASE_MS = 2_000;
/** Ceiling of the `/auth/me` backoff — from then on, one attempt every 30 s. */
export const AUTH_ME_RETRY_CAP_MS = 30_000;

/**
 * Backoff between `/auth/me` attempts during a hub outage: 2 s, 4 s, 8 s,
 * 16 s, then every 30 s. React Query passes the number of failures BEFORE
 * the one being retried (0 on the first retry).
 */
export function authMeRetryDelay(failureCount: number): number {
  return Math.min(AUTH_ME_RETRY_BASE_MS * 2 ** failureCount, AUTH_ME_RETRY_CAP_MS);
}

/**
 * React Query hook over `GET /auth/me`.
 *
 *   - `staleTime: 5min` — the agent record almost never changes mid-session.
 *   - `retry` — ONLY while the hub is out (unreachable, or it answered 5xx:
 *     `isHubOutageError`), and then unbounded with {@link authMeRetryDelay}
 *     — the boundary keeps the session and shows the outage screen meanwhile,
 *     so the console comes back by itself when the hub does (bug 5: a hub
 *     restart used to log out whoever reloaded). A 401 or any other 4xx is
 *     *data*, not a transient blip: never retried, so it lands cleanly on the
 *     axios 401 interceptor / the boundary on the first try.
 *     While retrying, React Query keeps the query `pending` (no data) or
 *     `success` (data kept) and exposes the last error as `failureReason` —
 *     RequireAuth keys the outage screen off that, not off `isError`.
 *   - `enabled` — only fire when a token is present. With no token we have
 *     nothing to send, and a fire-and-fail would just bounce through the 401
 *     handler unnecessarily.
 *
 * On success the agent is mirrored into the Zustand auth store so non-React
 * code (other api clients, devtools panels) can read it synchronously.
 */
export function useCurrentAgent(): UseQueryResult<AgentSummary, Error> {
  const accessToken = useAuthStore((s) => s.accessToken);
  const setSession = useAuthStore((s) => s.setSession);

  const query = useQuery<AgentSummary, Error>({
    queryKey: currentAgentQueryKey,
    queryFn: fetchCurrentAgent,
    staleTime: 5 * 60 * 1000,
    retry: (_failureCount, error) => isHubOutageError(error),
    retryDelay: authMeRetryDelay,
    enabled: accessToken !== null,
  });

  // Mirror fresh data into the auth store. Use an effect (rather than a side
  // effect inside `queryFn`) so we react to React Query's cache hits too.
  useEffect(() => {
    if (query.data && accessToken) {
      // Only call setSession when the data changed; otherwise we'd thrash
      // localStorage on every re-render.
      const current = useAuthStore.getState().agent;
      if (!current || current.id !== query.data.id || current.role !== query.data.role) {
        setSession({ accessToken, agent: query.data });
      }
    }
  }, [query.data, accessToken, setSession]);

  return query;
}
